import * as React from 'react';
import * as Color from '../consts/variables';
import { replacePlaceholders, __ } from '../helpers/TranslationService';
import { Discount } from './Discount';
import { CalamityFundChargeText } from './CalamityFundText';

let style: Record<string, React.CSSProperties> = {
    wrapper: {
        background: Color.lightGray,
        padding: 15,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        fontSize: 13,
    },
    price: {
        fontWeight: 'bold',
        fontSize: 30,
    },
    hotelServiceChargesLabel: {
        margin: '15px 15px 10px 15px',
        fontWeight: 'bold',
    },
    hint: {
        margin: '15px 15px 10px 15px',
        color: Color.black,
        fontSize: 12,
    },
    discountArrow: {
        display: 'inline-flex',
        flex: 1,
        alignSelf: 'center',
    },
    priceRows: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    priceOnRequest: {
        fontSize: 32,
        fontWeight: 700,
    },
    discountText: {
        color: Color.pink,
        fontWeight: 'bold',
        marginBottom: '7px',
    },
};

interface IProps {
    price: string | JSX.Element;
    discount: number;
    isEstimatedPrice?: boolean;
    withBackground?: boolean;
    isDualCurrency?: boolean;
    priceDefaultCurrency?: string;
    discountThreshold?: number;
    cabin?: string;
    discountType?: 'badge' | 'top';
    colorizePrice?: boolean;
    withFlight?: boolean;
    onRequest?: boolean;
    hasHotelServiceCharges?: boolean;
    market?: string;
}

export function PerPersonPriceBig(props: IProps) {
    const {
        discount,
        price,
        isEstimatedPrice,
        isDualCurrency,
        priceDefaultCurrency,
        discountThreshold,
        cabin,
        discountType = 'badge',
        colorizePrice = false,
        withFlight = false,
        onRequest = false,
        hasHotelServiceCharges,
        market,
    } = props;

    const hasDiscount = discountThreshold ? discount >= discountThreshold : discount > 0;
    const showDualCurrencyHint = isDualCurrency && Boolean(priceDefaultCurrency);

    const wrapperStyle = {
        ...style.wrapper,
        ...(props.withBackground ? {} : { backgroundColor: Color.white }),
        color: hasDiscount && colorizePrice ? Color.pink : Color.black,
    };

    if (!price || onRequest) {
        return (
            <div style={wrapperStyle}>
                <span style={style.price}>{price || __('on request', 'dreamlines')} </span>
            </div>
        );
    }

    if (isEstimatedPrice) {
        return (
            <div style={wrapperStyle}>
                <div style={style.priceOnRequest}>{__('priceOnRequest', 'dreamlines')}</div>
            </div>
        );
    }

    return (
        <div style={wrapperStyle}>
            <div style={style.priceRows}>
                {hasDiscount && discountType === 'top' && (
                    <div style={style.discountText}>
                        {replacePlaceholders(__('upToXDiscount', 'dreamlines'), [`-${discount}%`])}
                    </div>
                )}
                {cabin && <div>{cabin}</div>}
                <div style={{ display: 'inline-flex', alignItems: 'baseline' }}>
                    {hasDiscount && discountType === 'badge' && (
                        <div style={style.discountArrow}>
                            <Discount discount={discount} />
                        </div>
                    )}
                    {!isEstimatedPrice && (
                        <span style={{ marginRight: '5px' }}>{`${__('from', 'dreamlines')} `}</span>
                    )}
                    {isDualCurrency && `~`}
                    <span style={style.price}>{price}</span>
                    {isDualCurrency && `*`}
                    {/* Grows to center the price */}
                    {hasDiscount && discountType === 'badge' && (
                        <div style={{ display: 'flex', flex: 1 }} />
                    )}
                </div>
                <div>
                    <span>
                        {__('Price', 'dreamlines')}
                        {withFlight && ` ${__('with Flight', 'dreamlines')}`}
                    </span>
                </div>
                {hasHotelServiceCharges && (
                    <div style={style.hotelServiceChargesLabel}>
                        <span>{__('incl. hotel service charges', 'dreamlines')}</span>
                    </div>
                )}
                {market === 'dreamlines.nl' && <CalamityFundChargeText />}
                {showDualCurrencyHint && (
                    <div style={style.hint}>{`*${__(
                        'mobile.request.dualCurrencyHint',
                        'dreamlines',
                    )} ${priceDefaultCurrency}`}</div>
                )}
            </div>
        </div>
    );
}
